import { z } from 'zod';
import { contentIdSchema, isoDateSchema } from './common.js';
import { lessonStatusSchema } from './auth.js';
import { monthProgressSchema } from './progress.js';

/**
 * Contratos del contenido de las lecciones.
 *
 * Aquí hay DOS cosas distintas y conviene no confundirlas:
 *
 * 1. El CONTRATO DEL ARCHIVO (`lessonSchema`). Valida lo que escribes en
 *    `content/lecciones/*.js`. Vive en el paquete compartido para que el
 *    frontend pueda tipar contra él, pero su trabajo real es DOCUMENTAR EL
 *    FORMATO: si un mes nuevo trae un bloque que este schema no conoce, el
 *    seed lo falla al arrancar en vez de servirlo roto dentro de tres meses.
 *
 * 2. El CONTRATO DE LA RESPUESTA (`lessonSummarySchema`, `lessonDetailSchema`).
 *    Esto es lo que cruza la red, y es lo que el frontend de verdad valida.
 *
 * El contenido NO está en la base de datos y esta es la razón de todo el
 * módulo: una lección es código versionado, el progreso es dato. Mover una
 * lección de la semana 3 a la 4 es un commit de Git, no una migración que
 * alguien tiene que aplicar en tres entornos a la vez.
 */

// ─── Formato de los archivos de contenido ────────────────────────────────────

/**
 * `w01-l03` = bloque w01, lección 3.
 *
 * El id es una clave ESTABLE del progreso, no una posición: la lección que lo
 * lleva no cambia de identidad al reordenar el temario. El orden real lo manda
 * el campo `order` de cada lección (con `month` delante), y el id solo es el
 * desempate: las lecciones de arranque del mes 1 llevan ids altos (w01-l07…)
 * aunque se muestren primero, porque se añadieron después y renumerar las
 * antiguas rompería el progreso ya guardado. El cero del prefijo NO es
 * cosmético: sin él, ordenar por texto daría "w1-l10" antes que "w1-l2", que
 * es justo el error clásico.
 *
 * El prefijo es un NAMESPACE, no un mes: `w02-*` son las lecciones de
 * asincronía escritas cuando el mes 2 era ese, y conservan esos ids aunque el
 * temario los haya reordenado a otro mes. Los bloques nuevos toman su propio
 * prefijo (`web-l01`); así nunca colisionan con lo ya guardado.
 */
export const lessonIdSchema = z
  .string()
  .regex(/^[a-z0-9]{2,12}-l\d{2}$/, 'El id de lección debe tener el formato w01-l03 o web-l01');

/** "w01-l03-e2" = ejercicio 2 de esa lección. */
export const exerciseIdSchema = z
  .string()
  .regex(/^[a-z0-9]{2,12}-l\d{2}-e\d+$/, 'El id de ejercicio debe ser w01-l03-e2 o web-l01-e1');

const paragraphSchema = z.object({ type: z.literal('p'), text: z.string().min(1) });

const codeSchema = z.object({
  type: z.literal('code'),
  language: z.string().min(1).default('javascript'),
  caption: z.string().optional(),
  content: z.string().min(1),
});

const listSchema = z.object({
  type: z.literal('list'),
  /** `ordered: false` (por defecto) es una lista con viñetas. */
  ordered: z.boolean().default(false),
  items: z.array(z.string().min(1)).min(1),
});

/**
 * Aviso que se destaca del texto normal. Se usa para los errores frecuentes y
 * para las trampas que hacen perder horas a quien no las conoce de antemano.
 */
const noteSchema = z.object({
  type: z.literal('note'),
  tone: z.enum(['info', 'warning', 'danger']).default('info'),
  text: z.string().min(1),
});

/**
 * Los bloques son una unión discriminada, no un objeto con muchos opcionales.
 *
 * La diferencia importa: con un objeto normal, `{ type: 'code' }` sin `content`
 * pasaría la validación y reventaría al pintar. Con `discriminatedUnion`, Zod
 * exige los campos de ESE tipo y rechaza el resto. El error sale en el seed,
 * no en producción.
 *
 * Se evita Markdown a propósito: el contenido es JS y las lessons se pintan
 * como elementos de React. Sin `dangerouslySetInnerHTML` no hay superficie de
 * XSS por un `**` mal colocado, y la CSP puede seguir siendo estricta.
 */
export const lessonBlockSchema = z.discriminatedUnion('type', [
  paragraphSchema,
  codeSchema,
  listSchema,
  noteSchema,
]);

export const lessonSectionSchema = z.object({
  heading: z.string().min(1),
  blocks: z.array(lessonBlockSchema).min(1),
});

/**
 * Un test ejecutable de un ejercicio.
 *
 * La decisión importante: el test NO es código. Es una expresión escrita como
 * texto (`expresion`) y un valor esperado (`esperado`), más unas sentencias de
 * preparación. El runner genera el programa que evalúa eso.
 *
 * ¿Por qué no escribir los tests como código (`expect(sumar(2,3)).toBe(5)`)?
 * Porque haría falta `eval` o `new Function` para cargarlos, y la CSP de
 * producción los prohíbe a propósito: permitir `unsafe-eval` por comodidad del
 * runner equivaldría a permitir XSS en toda la aplicación. Con el test como
 * dato, el runner solo concatena cadenas y el navegador nunca evalúa nada.
 *
 * La comparación la hace el runner con un `igualProfundo` propio, porque
 * `JSON.stringify` no sirve: no distingue `undefined` de `null`, treats un
 * array disperso como `[null, null]`, y un `Map` se convierte en `{}`.
 *
 * @typedef {object} ExerciseTest
 * @property {string} [nombre] Lo que se está comprobando, en una frase.
 *   Opcional porque la ETIQUETA por defecto es la propia expresión, que ya dice
 *   qué se está probando: `vacia(null) === false` se entiende mejor que un
 *   nombre inventado. Se escribe `nombre` cuando hay algo que añadir.
 * @property {string} [preparacion] Sentencias a ejecutar antes de la expresión.
 *   Sirve para guardar el resultado: `const copia = agregar(notas, 3);`.
 * @property {string} expresion La expresión a comparar con lo esperado.
 * @property {unknown} [esperado] Lo que tiene que devolver. Si se omite, se
 *   espera `undefined`.
 */
export const exerciseTestSchema = z.object({
  nombre: z.string().min(1).optional(),
  preparacion: z.string().default(''),
  expresion: z.string().min(1),
  // `z.unknown()` acepta cualquier cosa, incluido `undefined`. Con
  // `.optional()` el campo podría no existir; con `z.unknown()` también se
  // acepta `esperado: undefined`, que es lo que escribe un autor de tests.
  esperado: z.unknown(),
});

export const lessonExerciseSchema = z.object({
  id: exerciseIdSchema,
  title: z.string().min(1),
  prompt: z.string().min(1),
  /** Pista. Se enseña a petición: el objetivo es que primero lo intentes. */
  hint: z.string().optional(),
  /**
   * Pista en código (2.º escalón de la ayuda). Fragmento pequeño, de una o dos
   * líneas, que acerca al alumno a la solución sin regalarla. Opcional: sin
   * ella, el 2.º escalón repite la pista de texto.
   */
  hintCodigo: z.string().optional(),
  /**
   * Solución completa (3.er escalón). Se ofrece SOLO después de, al menos, un
   * intento fallido y se pide con un clic explícito, para que un alumno que
   * acaba de escribir pruebe antes de rendirse. Opcional: sin ella se muestra
   * un mensaje invitando a reintentar y consultar Recursos.
   */
  solution: z.string().optional(),
  starterCode: z.string().min(1),
  /**
   * Qué tiene que cumplirse para darlo por bueno, en texto: lo que el alumno lee.
   */
  expected: z.string().min(1),
  /**
   * Los tests que el runner ejecuta de verdad.
   *
    * Vacío en las lecciones que aún no se han escrito, y por eso es opcional con
    * default: añadir el runner no puede romper el temario ya escrito. Cuando todos
    * los ejercicios del temario los tengan, esta lista deja de ser decorativa y pasa
    * a ser lo que decide si un ejercicio está resuelto.
   */
  tests: z.array(exerciseTestSchema).default([]),
});

/**
 * Una pregunta de autoevaluación al final de la lección.
 *
 * El quiz es opcional y de respuesta única: la `pregunta` pide elegir entre
 * `opciones`, y la corregida es la que ocupa el índice `correcta` (0-based).
 * Es autoevaluación, no examen: el alumno puede reintentar la pregunta que
 * haya fallado, y el resultado se guarda solo en el navegador.
 */
export const quizQuestionSchema = z.object({
  pregunta: z.string().min(1),
  opciones: z.array(z.string().min(1)).min(2),
  correcta: z.number().int().min(0),
  /** Se muestra cuando la pregunta está respondida, se acierte o no. */
  explicacion: z.string().optional(),
});

/** Un enlace externo de la sección "Para profundizar" de una lección. */
export const lessonSourceSchema = z.object({
  titulo: z.string().min(1),
  url: z.string().min(1),
  descripcion: z.string().optional(),
});

/** Una lección tal y como está en `content/lecciones/`. */
export const lessonSchema = z.object({
  id: lessonIdSchema,
  month: z.number().int().min(1).max(12),
  week: z.number().int().min(1).max(4),
  order: z.number().int().min(1),
  /**
   * Nivel pedagógico (0..6) al que pertenece la lección, según la estructura
   * de niveles del curso. El MES agrupa por orden temporal; el NIVEL dice qué
   * se necesita haber entendido antes. Opcional: las lecciones escritas antes
   * de existir el campo no tienen, y eso no las invalida.
   */
  nivel: z.number().int().min(0).max(6).optional(),
  title: z.string().min(1),
  summary: z.string().min(1),
  estimatedMinutes: z.number().int().min(5).max(600),
  objectives: z.array(z.string().min(1)).min(1),
  sections: z.array(lessonSectionSchema).min(1),
  exercises: z.array(lessonExerciseSchema),
  /** Autoevaluación de cierre de la lección, por preguntas. */
  quiz: z.array(quizQuestionSchema).optional(),
  /** Enlaces a documentación y práctica para seguir aprendiendo por fuera. */
  fuentes: z.array(lessonSourceSchema).optional(),
});

/** El temario completo. El seed lo valida entero antes de servir nada. */
export const lessonCatalogSchema = z.array(lessonSchema).min(1);

// ─── Respuestas de la API ────────────────────────────────────────────────────

/**
 * Resumen para la lista. NO lleva secciones ni ejercicios: el listado no
 * necesita ese peso, y pedirlo sería mandar por el cable el temario entero en
 * cada carga del panel para descartar el 90% en el cliente.
 */
export const lessonSummarySchema = z.object({
  id: contentIdSchema,
  month: z.number().int(),
  week: z.number().int(),
  order: z.number().int(),
  title: z.string(),
  summary: z.string(),
  estimatedMinutes: z.number().int(),
  exerciseCount: z.number().int(),
  status: lessonStatusSchema,
  completedAt: isoDateSchema.nullable(),
});

export const lessonDetailSchema = lessonSummarySchema.extend({
  objectives: z.array(z.string()),
  sections: z.array(lessonSectionSchema),
  exercises: z.array(lessonExerciseSchema),
  /** Autoevaluación de cierre de la lección, por preguntas. */
  quiz: z.array(quizQuestionSchema).optional(),
  /** Enlaces a documentación y práctica. */
  fuentes: z.array(lessonSourceSchema).optional(),
  /** Siguiente lección del temario, para el botón "continuar". */
  nextId: contentIdSchema.nullable(),
});

/**
 * Una sola respuesta con todo lo que el panel necesita.
 *
 * La lección, el temario agrupado y el avance van juntos a propósito: son el
 * mismo dato visto de tres maneras, y separarlos obligaría al frontend a
 * coordinate tres estados de carga para pintar una pantalla que en realidad
 * muestra una sola cosa.
 */
export const lessonListResponseSchema = z.object({
  lessons: z.array(lessonSummarySchema),
  months: z.array(monthProgressSchema),
});

// ─── Tipos que viajan por la red ─────────────────────────────────────────────

/**
 * @typedef {object} LessonSummary
 * @property {string} id
 * @property {number} month
 * @property {number} week
 * @property {number} order
 * @property {string} title
 * @property {string} summary
 * @property {number} estimatedMinutes
 * @property {number} exerciseCount
 * @property {'PENDING' | 'IN_PROGRESS' | 'COMPLETED'} status
 * @property {string | null} completedAt ISO 8601.
 */

/**
 * @typedef {object} LessonDetail
 * @property {string[]} objectives
 * @property {import('./lessons.js').LessonSection[]} sections
 * @property {import('./lessons.js').LessonExercise[]} exercises
 * @property {import('./lessons.js').QuizQuestion[]} [quiz]
 * @property {import('./lessons.js').LessonSource[]} [fuentes]
 * @property {string | null} nextId
 */

/**
 * @typedef {object} LessonBlock
 * @property {'p' | 'code' | 'list' | 'note'} type
 */

/**
 * @typedef {object} LessonSection
 * @property {string} heading
 * @property {LessonBlock[]} blocks
 */

/**
 * @typedef {object} LessonExercise
 * @property {string} id
 * @property {string} title
 * @property {string} prompt
 * @property {string} [hint]
 * @property {string} [hintCodigo]
 * @property {string} [solution]
 * @property {string} starterCode
 * @property {string} expected
 * @property {ExerciseTest[]} tests
 */

/**
 * @typedef {object} QuizQuestion
 * @property {string} pregunta
 * @property {string[]} opciones
 * @property {number} correcta Índice (0-based) de la opción correcta.
 * @property {string} [explicacion]
 */

/**
 * @typedef {object} LessonSource
 * @property {string} titulo
 * @property {string} url
 * @property {string} [descripcion]
 */

/**
 * @typedef {object} LessonProgress
 * @property {string} lessonId
 * @property {'PENDING' | 'IN_PROGRESS' | 'COMPLETED'} status
 * @property {string | null} completedAt ISO 8601.
 */

/**
 * @typedef {object} LessonListResponse
 * @property {LessonSummary[]} lessons
 * @property {import('./progress.js').MonthProgress[]} months
 */
